import React from 'react';

/**
 * Shared display helpers for the monthly owner report.
 *
 * Feature-local on purpose, for now. `utils/numberFormatter.js` only offers
 * Arabic-Indic digits, and the report renders figures in Western digits to
 * reconcile with the /finance dashboard — so there was nothing shared to import.
 * `PerformanceMetrics.js` and `PlatformBreakdown.js` each carry their own copy of
 * the same two functions; consolidating all of them into `numberFormatter.js` is
 * the agreed follow-up pass (tasks/owner-monthly-report.md §16.7). This module
 * exists so the report does not add a third and fourth copy in the meantime.
 */

/** Grouped, up to 2 decimals, Western digits — the /finance dashboard's rule. */
export const formatNumber = (n) =>
  (Number.isFinite(n) ? n : 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

export const formatPercent = (n) => `${formatNumber(n)}%`;

/**
 * The unit's P-000x code, beside its name.
 *
 * `dir="ltr"` is required, not cosmetic: the document is `<html lang="ar" dir="rtl">`,
 * and a Latin letter + neutral hyphen + digits sitting against Arabic text is exactly
 * the bidi case that reorders. Every other property-code badge in the app sets it
 * (SecurePropertyDetails, BookingDetails, GuestDetailsPage) — this matches them.
 *
 * NOT passed through formatNumber: it is an identifier, not a quantity.
 *
 * Returns null when absent, which is the normal state for a snapshot generated
 * before schemaVersion 2 — those reports are frozen and simply have no code.
 */
export const UnitCode = ({ code }) =>
  code ? <span className="report-unit-code" dir="ltr">{code}</span> : null;

/**
 * Month-over-month movement.
 *
 * NOT RENDERED ANYWHERE as of 2026-08-01 — removed from the metric cards and from the
 * league table on request. Kept because `comparison.*` is still on every snapshot and
 * the direction question may come back; delete this if it has not by the time the
 * report reaches its second owner.
 *
 * `neutral` renders as words, never as a coloured arrow: an unchanged metric is
 * not a decline, and the analyzer distinguishes the two precisely so the UI can.
 *
 * @param {Object} movement - { change, direction } from revenueAnalyzer.calculateComparison
 * @param {boolean} compact - arrow + number only, for a table cell
 */
export const MovementBadge = ({ movement, compact = false }) => {
  if (!movement) return null;

  const { change, direction } = movement;

  if (direction === 'neutral') {
    return (
      <span className="report-movement is-neutral">
        {compact ? '—' : 'بلا تغيير'}
      </span>
    );
  }

  const up = direction === 'up';

  return (
    <span className={`report-movement ${up ? 'is-up' : 'is-down'}`}>
      <i className={`bi ${up ? 'bi-arrow-up' : 'bi-arrow-down'}`} aria-hidden="true"></i>
      {formatPercent(Math.abs(change))}
    </span>
  );
};
